/**
 * Session lifecycle for the Observ RUM SDK (FR-1).
 *
 * Produces a single, stable `session.id` per visit and exposes it INTERNALLY as
 * the one source of truth that later modules (`rrweb-record`, `semantic-events`,
 * `otel-rum`) stamp onto every signal. Not part of the public `observ` surface.
 *
 * Authoritative rules (architecture D5):
 *   - `session.id` is a UUID v4, generated in the browser,
 *   - persisted in `sessionStorage` (per-tab — two tabs are two visits, by design;
 *     opt into cross-tab sharing via {@link configureSession} `crossTab`),
 *   - rotated after 30 min of inactivity OR a 4 h hard duration cap,
 *   - on rotation a fresh id is minted and chained to the prior one via
 *     `previousId`, so the backend can stitch consecutive sessions of one visit.
 *
 * Rotation is LAZY: there is no background timer in THIS module — the window is
 * evaluated on the next `ensureSession`/`getSessionId`/`touchSession` call, and a
 * fresh id is minted then if a window has elapsed. The active-session sweep timer,
 * the `session.start`/`session.end` event emission and the activity listeners live
 * in `session-lifecycle.ts`, which subscribes via {@link addSessionListener}.
 *
 * This module performs NO network I/O. It must never throw: if `sessionStorage`/
 * `localStorage` or `crypto` are unavailable (private mode, disabled storage,
 * SSR / insecure context), it degrades to an in-memory session.id. NOTE: in that
 * degraded mode persistence is lost across full page loads, so each navigation
 * starts a new session (the per-visit guarantee holds only while storage works).
 */
import { generateUuid } from './random-id.js'

/** Single storage key holding the serialized session state. */
export const SESSION_STORAGE_KEY = 'observ.rum.session'

/**
 * Default inactivity window after which a new `session.id` is minted (architecture
 * D5, resolves PRD Open Q2). 30 minutes, in milliseconds. Overridable per app via
 * {@link configureSession}.
 */
export const SESSION_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000

/**
 * Default hard cap on a single session's total duration, regardless of activity
 * (parity with the Kapitalvins reference). 4 hours, in milliseconds. A session
 * older than this rotates even under continuous use, bounding how long one id can
 * follow a visitor. Overridable via {@link configureSession}.
 */
export const SESSION_MAX_DURATION_MS = 4 * 60 * 60 * 1000

/** Shape persisted under {@link SESSION_STORAGE_KEY}. Timestamps are epoch ms. */
interface PersistedSession {
  id: string
  startedAt: number
  lastActivityAt: number
  /** Id of the session this one replaced, set on every rotation (chaining). */
  previousId?: string
  /** Epoch ms at which a `session.end` was already emitted for this id (so the
   *  sweep/pagehide end and the rotation end are never emitted twice). */
  endedAt?: number
}

/**
 * One session lifecycle transition, published to subscribers of
 * {@link addSessionListener}. `start` fires when a fresh id is minted (first visit
 * or a rotation); `end` fires once per session, when it expires (rotation,
 * inactivity sweep, or pagehide).
 */
export type SessionLifecycleEvent =
  | { type: 'start'; id: string; previousId?: string }
  | { type: 'end'; id: string; durationMs: number }

/**
 * In-memory memoization. Within a single page it is the source of truth (one
 * `init` per SPA load); it is also the sole fallback when storage is unusable.
 */
let current: PersistedSession | null = null

/** Effective windows + storage backend, mutable via {@link configureSession}. */
let inactivityTimeoutMs = SESSION_INACTIVITY_TIMEOUT_MS
let maxDurationMs = SESSION_MAX_DURATION_MS
let storageMode: 'session' | 'local' = 'session'

/** Lifecycle subscribers (see {@link addSessionListener}). */
const listeners = new Set<(event: SessionLifecycleEvent) => void>()

/**
 * Tune the session windows and storage scope. Called once by `init` from the
 * public options; no-op for any field left undefined. `crossTab: true` switches
 * the backing store from `sessionStorage` (per-tab, the default) to
 * `localStorage`, letting the same visitor's tabs share one session — opt-in, so
 * the historical per-tab model is preserved unless explicitly requested.
 */
export function configureSession(opts: {
  inactivityTimeoutMs?: number
  maxDurationMs?: number
  crossTab?: boolean
}): void {
  if (typeof opts.inactivityTimeoutMs === 'number' && opts.inactivityTimeoutMs > 0) {
    inactivityTimeoutMs = opts.inactivityTimeoutMs
  }
  if (typeof opts.maxDurationMs === 'number' && opts.maxDurationMs > 0) {
    maxDurationMs = opts.maxDurationMs
  }
  if (typeof opts.crossTab === 'boolean') {
    storageMode = opts.crossTab ? 'local' : 'session'
  }
}

/**
 * Subscribe to session lifecycle transitions. Returns an unsubscribe fn. Listeners
 * are invoked synchronously inside `ensureSession`/`endSessionIfExpired`; each call
 * is guarded so a throwing subscriber can never break session resolution.
 */
export function addSessionListener(fn: (event: SessionLifecycleEvent) => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

function notify(event: SessionLifecycleEvent): void {
  for (const fn of listeners) {
    try {
      fn(event)
    } catch {
      /* swallow — a subscriber must never break session resolution */
    }
  }
}

/** The active storage backend (per-tab by default, shared when `crossTab`). */
function backend(): Storage | undefined {
  try {
    return storageMode === 'local' ? globalThis.localStorage : globalThis.sessionStorage
  } catch {
    return undefined
  }
}

/** Read from the active storage, swallowing any access error. */
function safeRead(): string | null {
  try {
    return backend()?.getItem(SESSION_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

/** Write to the active storage, swallowing any access/quota error. */
function safeWrite(value: string): void {
  try {
    backend()?.setItem(SESSION_STORAGE_KEY, value)
  } catch {
    // In-memory `current` stays authoritative; nothing else to do.
  }
}

/** Validate the parsed storage payload before trusting it. */
function isPersistedSession(value: unknown): value is PersistedSession {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.id === 'string' &&
    v.id.length > 0 &&
    typeof v.startedAt === 'number' &&
    Number.isFinite(v.startedAt) &&
    typeof v.lastActivityAt === 'number' &&
    Number.isFinite(v.lastActivityAt)
  )
}

/** Resolve the current session, from memory first, then storage. */
function load(): PersistedSession | null {
  if (current) return current
  const raw = safeRead()
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return isPersistedSession(parsed) ? parsed : null
  } catch {
    return null // corrupt JSON → treat as absent
  }
}

/** Memoize in memory and best-effort persist to storage. */
function save(session: PersistedSession): void {
  current = session
  safeWrite(JSON.stringify(session))
}

/**
 * True when `session` has crossed the inactivity window OR the hard duration cap.
 * Both deltas are clamped to 0 so a backward clock jump (now < a stored timestamp,
 * e.g. NTP/DST) can't yield a negative "elapsed" that silently extends OR
 * prematurely expires the session.
 */
export function isExpired(session: PersistedSession | null, now: number): boolean {
  if (!session) return true
  const inactiveFor = Math.max(0, now - session.lastActivityAt)
  const ageFor = Math.max(0, now - session.startedAt)
  return inactiveFor > inactivityTimeoutMs || ageFor > maxDurationMs
}

/**
 * Ensure a `session.id` exists for this visit and return it.
 *
 * Reuses the stored id when still within both windows (refreshing
 * `lastActivityAt`); otherwise ends the expiring session (unless a `session.end`
 * was already emitted by the sweep) and mints a fresh, chained session — emitting
 * `start`/`end` to subscribers. `now` is injectable for deterministic tests.
 */
export function ensureSession(now: number = Date.now()): string {
  const existing = load()
  if (existing && !isExpired(existing, now)) {
    save({ ...existing, lastActivityAt: now })
    return existing.id
  }
  // Expired (or none) → mint a fresh session chained to the previous one.
  if (existing && existing.endedAt == null) {
    notify({ type: 'end', id: existing.id, durationMs: Math.max(0, now - existing.startedAt) })
  }
  const fresh: PersistedSession = {
    id: generateUuid(),
    startedAt: now,
    lastActivityAt: now,
    ...(existing ? { previousId: existing.id } : {}),
  }
  save(fresh)
  notify({ type: 'start', id: fresh.id, ...(existing ? { previousId: existing.id } : {}) })
  return fresh.id
}

/**
 * Return the `session.id` for the current visit.
 *
 * Delegates to {@link ensureSession} so every read enforces both windows
 * (rotating to a fresh id once a window has elapsed) — a memoized read must not be
 * able to hand back a session that should have rotated. As a consequence a read
 * also refreshes `lastActivityAt`, i.e. reading the id counts as activity.
 */
export function getSessionId(now: number = Date.now()): string {
  return ensureSession(now)
}

/**
 * Push the inactivity window forward on real activity (rotating, and emitting
 * `end`/`start`, if the session has already expired). The activity listeners in
 * `session-lifecycle.ts` call this; reads also count as activity via
 * {@link ensureSession}.
 */
export function touchSession(now: number = Date.now()): void {
  ensureSession(now)
}

/**
 * Emit `session.end` for the active session IFF it has expired and no end was
 * emitted yet, marking it ended so neither the next rotation nor a later
 * sweep/pagehide re-emits it. Used by the lifecycle sweep timer + pagehide hook so
 * a session that simply goes idle (no further activity to trigger a lazy rotation)
 * still produces a timely `session.end`. Does NOT rotate — the next activity does.
 */
export function endSessionIfExpired(now: number = Date.now()): void {
  const session = load()
  if (!session || session.endedAt != null || !isExpired(session, now)) return
  save({ ...session, endedAt: now })
  notify({ type: 'end', id: session.id, durationMs: Math.max(0, now - session.startedAt) })
}

/**
 * Re-read the persisted session from storage into memory, bypassing the in-memory
 * memo. Used by the cross-tab `storage`-event handler so this tab adopts the
 * session another tab just wrote (only meaningful when `crossTab` is enabled).
 */
export function adoptStoredSession(): void {
  const raw = safeRead()
  if (raw === null) return
  try {
    const parsed: unknown = JSON.parse(raw)
    if (isPersistedSession(parsed)) current = parsed
  } catch {
    /* corrupt cross-tab write → keep our own state */
  }
}

/**
 * @internal Test-only. Clears the in-memory memoization to simulate a fresh page
 * load / module re-import (storage is left untouched), drops all lifecycle
 * subscribers, and restores the default windows / per-tab storage so config from
 * one test can't leak into the next.
 */
export function resetSessionState(): void {
  current = null
  listeners.clear()
  inactivityTimeoutMs = SESSION_INACTIVITY_TIMEOUT_MS
  maxDurationMs = SESSION_MAX_DURATION_MS
  storageMode = 'session'
}
